// src/pages/OrderEdit.tsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Customer, Product, toCapitalCase, displayPrice, OrderItemData, ProductSelectionModalProps, OrderItem } from '../utils/types';
import { useCustomerContext } from '../context/customerContext';
import CreateCustomerModal from '../components/CreateCustomerModal';
import {
    createUser,
    createCustomer,
    fetchProducts4manual,
    fetchMtPrice,
    createOrder,
    updateManualOrder,
    fetchOrderDetails,
    fetchCustomer, // Import fetchCustomer
    createNewProductItem,
    fetchSoftDeletedOrderItems, // Placeholder for new API function
    restoreOrderItemAPI,      // Placeholder for new API function
    updateOrder
} from '../utils/api';
import Pagination from '../components/Pagination';
import { toast } from 'react-toastify';
import { FiX, FiUserPlus, FiList, FiPlusCircle, FiTrash2, FiRotateCcw } from 'react-icons/fi';

import { useAuth } from '../context/AuthContext';
import Spinner from '../components/Spinner'; // Import the Spinner component


// Helper type (already in DeletedItemsModal.tsx, but good for reference here)
interface DeletedItemDisplay extends OrderItemData {
    orderItemId: number;
    deletedAt?: string;
}

const ITEMS_PER_PAGE = 10;
const PRODUCTS_PER_PAGE_MODAL = 8;
const AUTO_SAVE_INTERVAL = 30000; // 30 seconds

const OrderEdit: React.FC = () => {

    // --- Existing State and Hooks ---

    const { t } = useTranslation();
    const { customers: allCustomers, loading: loadingAllCustomers, error: errorAllCustomers, refreshCustomers } = useCustomerContext();

    const { orderId: routeOrderId } = useParams<{ orderId?: string }>();
    const navigate = useNavigate();

    const { user: loggedInUser } = useAuth();

    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    const [isCustomerSelectorVisible, setIsCustomerSelectorVisible] = useState<boolean>(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);

    const [orderItems, setOrderItems] = useState<OrderItemData[]>([]);

    const [itemJustAdded, setItemJustAdded] = useState(false); // Flag to trigger save on add
    const [itemJustRemoved, setItemJustRemoved] = useState(false); // Flag to trigger save on remove
    const [itemJustUpdated, setItemJustUpdated] = useState(false); // Flag to trigger save on update
    const [itemJustRestored, setItemJustRestored] = useState(false); // Flag to trigger save on restore


    const [isProductSelectorVisible, setIsProductSelectorVisible] = useState<boolean>(false);

    const [savedOrderId, setSavedOrderId] = useState<number | null>(null);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isDirty, setIsDirty] = useState(false); // To track unsaved changes

    const [isTabActive, setIsTabActive] = useState(true); // Track tab activity

    const [isLoadingDraft, setIsLoadingDraft] = useState<boolean>(false);

    const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null); // For debouncing saves


    const [customerSearchTerm, setCustomerSearchTerm] = useState<string>('');
    const [customerCurrentPage, setCustomerCurrentPage] = useState<number>(1);

    // const [lastRemovedItem, setLastRemovedItem] = useState<OrderItemData | null>(null);

    // --- State for Deleted Items Modal ---
    const [isDeletedItemsModalVisible, setIsDeletedItemsModalVisible] = useState<boolean>(false);


    // --- Fulfillment State ---
    const [fulfillmentType, setFulfillmentType] = useState<'delivery' | 'pickup'>('delivery');
    const [pickupTime, setPickupTime] = useState('');
    const [pickerName, setPickerName] = useState('');
    const [pickerPhone, setPickerPhone] = useState('');
    const [pickerCarPlate, setPickerCarPlate] = useState('');
    const [isSavingFulfillment, setIsSavingFulfillment] = useState(false);

    // --- End Fulfillment State ---

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isFulfillmentModalOpen, setIsFulfillmentModalOpen] = useState(false);

    // Add a state variable to track tab activity
    const [isAutoSaveEnabled, setIsAutoSaveEnabled] = useState(true);

    // Add visibility change listener
    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsTabActive(!document.hidden);
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        // --- Cleanup timers on unmount ---
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            if (autoSaveTimerRef.current) clearInterval(autoSaveTimerRef.current);
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, []);

    // Disable auto-save if the tab is inactive
    useEffect(() => {
        if (!isTabActive) {
            console.log('Tab is inactive, pausing auto-save.');
            setIsAutoSaveEnabled(false);
        } else {

            console.log('Tab is active, resuming auto-save.');
            setIsAutoSaveEnabled(true);
        }
    }, [isTabActive]);

    // --- Determine if any background action is in progress ---
    const isActionInProgress = isSaving || isLoadingDraft || loadingAllCustomers;

    // =================================================================================
    // 🚚 UI Component: Fulfillment Modal
    // =================================================================================
    const FulfillmentModal: React.FC<any> = ({
        isOpen, onClose, fulfillmentType, setFulfillmentType, pickupTime, setPickupTime,
        pickerName, setPickerName, pickerPhone, setPickerPhone, pickerCarPlate, setPickerCarPlate,
        handleUpdateFulfillment, isSavingFulfillment, isActionInProgress
    }) => {
        if (!isOpen) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center" onClick={onClose}>
                <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg m-4" onClick={e => e.stopPropagation()}>
                    <div className="flex justify-between items-center mb-4 border-b pb-3">
                        <h2 className="text-xl font-bold text-gray-800">Update Fulfillment Details</h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl font-bold">&times;</button>
                    </div>

                    <div className="space-y-4">
                        {/* Fulfillment Method Selection */}
                        <div className="flex space-x-4 mb-4">
                            <label className="flex items-center space-x-2 cursor-pointer">
                                <input type="radio" name="fulfillment" value="delivery" checked={fulfillmentType === 'delivery'} onChange={() => setFulfillmentType('delivery')} className="form-radio h-4 w-4 text-blue-600" />
                                <span className="font-medium">Delivery</span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                                <input type="radio" name="fulfillment" value="pickup" checked={fulfillmentType === 'pickup'} onChange={() => setFulfillmentType('pickup')} className="form-radio h-4 w-4 text-blue-600" />
                                <span className="font-medium">Self Pickup</span>
                            </label>
                        </div>

                        {/* Conditional Pickup Details Form */}
                        {fulfillmentType === 'pickup' && (
                            <div className="p-4 border-t border-gray-200 space-y-3 animate-fade-in">
                                <h4 className="font-semibold text-md text-gray-700">Pickup Details</h4>
                                <div>
                                    <label htmlFor="pickupDate" className="block text-sm font-medium text-gray-600">Estimated Pickup Date & Time</label>
                                    <div className="flex space-x-2 mt-1">
                                        <input
                                            type="date"
                                            id="pickupDate"
                                            value={pickupTime.split('T')[0] || ''}
                                            onChange={e => {
                                                const currentTime = pickupTime.split('T')[1] || '08:00';
                                                setPickupTime(`${e.target.value}T${currentTime}`);
                                            }}
                                            className="block w-1/2 p-2 border border-gray-300 rounded-md shadow-sm text-sm"
                                        />
                                        <select
                                            id="pickupTimeSlot"
                                            value={pickupTime.split('T')[1] || '08:00'}
                                            onChange={e => {
                                                const currentDate = pickupTime.split('T')[0] || new Date().toISOString().split('T')[0];
                                                setPickupTime(`${currentDate}T${e.target.value}`);
                                            }}
                                            className="block w-1/2 p-2 border border-gray-300 rounded-md shadow-sm text-sm"
                                        >
                                            {Array.from({ length: (18 - 8) * 4 + 1 }, (_, i) => {
                                                const totalMinutes = 8 * 60 + i * 15;
                                                const hour = Math.floor(totalMinutes / 60);
                                                const minute = totalMinutes % 60;
                                                if (hour > 18 || (hour === 18 && minute > 0)) return null;
                                                const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                                                return <option key={time} value={time}>{time}</option>;
                                            })}
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="pickerName" className="block text-sm font-medium text-gray-600">Picker's Name</label>
                                    <input type="text" id="pickerName" placeholder="e.g., John Doe" value={pickerName} onChange={e => setPickerName(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm text-sm" />
                                </div>
                                <div>
                                    <label htmlFor="pickerPhone" className="block text-sm font-medium text-gray-600">Picker's Phone</label>
                                    <input type="tel" id="pickerPhone" placeholder="e.g., 0123456789" value={pickerPhone} onChange={e => setPickerPhone(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm text-sm" />
                                </div>
                                <div>
                                    <label htmlFor="pickerCarPlate" className="block text-sm font-medium text-gray-600">Car Plate Number</label>
                                    <input type="text" id="pickerCarPlate" placeholder="e.g., WXX 1234" value={pickerCarPlate} onChange={e => setPickerCarPlate(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm text-sm" />
                                </div>
                            </div>
                        )}

                        {fulfillmentType === 'delivery' && (
                            <div className="p-4 border-t border-gray-200">
                                <p className="text-sm text-gray-600">The order will be delivered to the customer's registered address.</p>
                            </div>
                        )}

                        {/* Save Button */}
                        <div className="mt-6 pt-4 border-t flex justify-end">
                            <button onClick={handleUpdateFulfillment} disabled={isSavingFulfillment || isActionInProgress} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed">
                                {isSavingFulfillment ? 'Saving...' : 'Update Fulfillment'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // =================================================================================

    // We'll use a more specific type for items in the deleted modal, including their original order_item_id
    const [deletedOrderItems, setDeletedOrderItems] = useState<DeletedItemDisplay[]>([]);
    const [loadingDeletedItems, setLoadingDeletedItems] = useState<boolean>(false);

    // const [lastRemovedItem, setLastRemovedItem] = useState<OrderItemData | null>(null); // Replaced by DeletedItemsModal

    const filteredCustomersForSelection = useMemo(() => {
        if (!customerSearchTerm) {
            return allCustomers;
        }
        const lowerCaseSearchTerm = customerSearchTerm.toLowerCase();
        return allCustomers.filter(customer =>
            customer?.name?.toLowerCase().includes(lowerCaseSearchTerm) ||
            customer?.email?.toLowerCase().includes(lowerCaseSearchTerm) ||
            customer?.phone?.toLowerCase().includes(lowerCaseSearchTerm) ||
            customer?.id?.toString().includes(lowerCaseSearchTerm)
        );
    }, [allCustomers, customerSearchTerm]);

    const totalCustomerPages = Math.ceil(filteredCustomersForSelection.length / ITEMS_PER_PAGE);

    const currentCustomersForSelection = useMemo(() => {
        const startIndex = (customerCurrentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredCustomersForSelection.slice(startIndex, endIndex);
    }, [filteredCustomersForSelection, customerCurrentPage]);

    const handleCustomerSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setCustomerSearchTerm(event.target.value);
        setCustomerCurrentPage(1);
    }, []);

    const handleSelectCustomer = (customer: Customer) => {
        setSelectedCustomer(customer);
        setIsCustomerSelectorVisible(false);
        toast.success(t('orderEdit.customerSelected', { name: customer?.name }) as string);
    };

    const handleOpenCreateModal = () => setIsCreateModalOpen(true);

    const handleCloseCreateModal = () => setIsCreateModalOpen(false);

    const handleCreateCustomer = async (name: string, email: string, phone: string) => {
        try {
            const newUser = await createUser({ name, email, phone });
            // if (newUser && newUser.id) {
            if (newUser?.id) {

                await createCustomer({ user_id: newUser.id, name, email, phone });
                refreshCustomers();
            } else {
                throw new Error(t('orderEdit.errors.failedNewUserId'));
            }
        } catch (error) {
            console.error("Error creating customer:", error);
            toast.error(t('orderEdit.errors.failedToCreateCustomer', { error: error instanceof Error ? error.message : t('errors.unknownError') }) as string);
        }
    };

    useEffect(() => {
        const loadDraftOrder = async (id: number) => {
            if (loadingAllCustomers || (allCustomers.length === 0 && id !== 0)) { // id !== 0 check to allow proceeding if allCustomers is empty but we are creating a new order
                console.log("loadDraftOrder: Waiting for allCustomers to load or populate.");
                return;
            }

            setIsLoadingDraft(true);
            try {
                const draftOrder = await fetchOrderDetails(id);

                // console.log('Draft Order Details:', draftOrder);

                if (
                    draftOrder &&
                    ['draft', 'manual_order'].includes(draftOrder.status)
                ) {
                    setSavedOrderId(draftOrder.id);
                    const customer = allCustomers.find(c => c.id === draftOrder.customer_id);

                    if (customer) {
                        setSelectedCustomer(customer);
                    } else {
                        // console.warn(`Customer ID ${draftOrder.customer_id} not found in context for draft order.`);
                        // toast.error(`Customer (ID: ${draftOrder.customer_id}) for this draft order was not found. Please select a customer?.`);
                        // Don't navigate away, allow user to select a customer if the draft's customer is missing
                        // navigate('/new-order', { replace: true });
                        // return;
                        // Customer not in current context, try fetching them directly
                        console.warn(`Customer ID ${draftOrder.customer_id} not found in local context for draft order. Attempting to fetch.`);

                        try {
                            const fetchedCustomer = await fetchCustomer(draftOrder.customer_id);
                            if (fetchedCustomer) {
                                setSelectedCustomer(fetchedCustomer);
                                toast.info(t('orderEdit.customerLoadedForDraft', { name: fetchedCustomer?.name, id: draftOrder.customer_id }) as string, { toastId: `required-${draftOrder.customer_id}` });

                            } else {
                                // This case implies fetchCustomer returned null/undefined but didn't throw for a 404
                                throw new Error(t('orderEdit.errors.customerNotFoundOnServer', { id: draftOrder.customer_id }));
                            }
                        } catch (fetchError: any) {
                            // Fetch failed (e.g., 404 Not Found, or other server error)
                            console.error(`Failed to fetch customer ID ${draftOrder.customer_id} for draft:`, fetchError);
                            toast.error(t('orderEdit.errors.originalCustomerLoadFailed', { id: draftOrder.customer_id }) as string);
                            // UI will prompt user to select a customer, selectedCustomer remains null.
                        }
                    }

                    const itemsData: OrderItemData[] = draftOrder.items.map((item: OrderItem) => ({
                        orderItemId: item.id, // <-- PRESERVE order_item.id

                        productId: Number(item.product_id),
                        productName: item.payment_for || item.product_name, // Use payment_for if available, otherwise product_name
                        // productCode: item.product_code,
                        productCode: item.payment_desc, //abb 250610 IMPORTANT
                        quantity: Number(item.qty),
                        // unitPrice: item.price,
                        unitPrice: Number(item.unit_price), // Use unit_price for consistency and accuracy

                        // totalPrice: Number(item.product_value),
                        totalPrice: Number(item.unit_price) * Number(item.qty), // Calculate total price from unit_price and quantity
                        // productValue: Number(item.product_value), // Use product_value if available, otherwise calculate from unit_price
                        productValue: Number(item.product_value),
                    }));
                    setOrderItems(itemsData);
                    setIsDirty(false); // A freshly loaded draft is not dirty

                    toast.info(t('orderEdit.draftLoaded', { id: draftOrder.id }) as string, { toastId: `required-${draftOrder.id}` });
                } else {
                    toast.warn(t('orderEdit.orderNotFoundOrNotDraft', { id }) as string);
                    setSavedOrderId(null);
                    setSelectedCustomer(null);
                    setOrderItems([]);
                    navigate('/new-order', { replace: true });
                }
            } catch (err: any) {
                console.error(`Failed to load draft order ${id}:`, err); // Keep console.error in English for developers
                toast.error(t('orderEdit.errors.failedToLoadDraft', { error: err.message || t('errors.unknownError') }) as string);
                setSavedOrderId(null);
                setSelectedCustomer(null);
                setOrderItems([]);
                navigate('/new-order', { replace: true });
            } finally {
                setIsLoadingDraft(false);
            }
        };

        if (routeOrderId && !savedOrderId && !isLoadingDraft) {
            const id = parseInt(routeOrderId, 10);
            if (!isNaN(id)) {
                loadDraftOrder(id);
            } else {
                toast.error(t('orderEdit.errors.invalidOrderIdUrl') as string);
                navigate('/new-order', { replace: true });
            }
        }
    }, [routeOrderId, savedOrderId, allCustomers, loadingAllCustomers, navigate, isLoadingDraft]);

    const handleAddProductToOrder = (product: Product, quantity: number, price: number) => {
        setOrderItems(prevItems => {
            setIsDirty(true);

            const existingItemIndex = prevItems.findIndex(item => item.productId === product.id);
            if (existingItemIndex > -1) {
                const updatedItems = [...prevItems];
                const newQuantity = updatedItems[existingItemIndex].quantity + quantity;
                updatedItems[existingItemIndex] = {
                    ...updatedItems[existingItemIndex],
                    quantity: newQuantity,
                    totalPrice: newQuantity * updatedItems[existingItemIndex].unitPrice,
                };
                toast.info(t('orderEdit.quantityUpdated', { name: product.product_name }) as string);
                return updatedItems;
            } else {
                toast.success(t('orderEdit.itemAdded', { name: product.product_name }) as string);
                setItemJustAdded(true); // Set flag to trigger save
                return [
                    ...prevItems,
                    {
                        productId: product.id,
                        productName: product.product_name,
                        // productCode: product.product_code,
                        productCode: product.product_code,
                        quantity: quantity,
                        unitPrice: price,
                        totalPrice: quantity * price,
                        productValue: product.product_value,
                    },
                ];
            }
        });
        setIsProductSelectorVisible(false);
    };

    const calculateOrderTotal = useMemo(() => {

        // console.log('Calculating order total...', orderItems);

        return orderItems.reduce((total, item) => total + item.totalPrice, 0);
    }, [orderItems]);

    const handleSaveOrder = useCallback(async (isAutoSave = false) => {
        if (!selectedCustomer) {
            if (!isAutoSave) toast.error(t('orderEdit.errors.selectCustomerToSave') as string);
            return;
        }
        if (orderItems.length === 0) {
            if (!isAutoSave) toast.error(t('orderEdit.errors.addItemsToSave') as string);
            return;
        }
        if (!loggedInUser || !loggedInUser.id) {
            if (!isAutoSave) toast.error(t('orderEdit.errors.notLoggedInToSave') as string);
            return;
        }

        setIsSaving(true);

        const orderItemsPayload: OrderItem[] = orderItems.map((item): OrderItem => ({

            id: item.orderItemId, // <-- SEND BACK order_item.id if it exists


            order_id: 0, // Will be set by backend
            customer_id: selectedCustomer?.id, // Use the selected customer ID
            product_id: item.productId,
            product_name: item.productName,
            product_code: item.productCode,
            quantity: Number(item.quantity),
            unit_price: Number(item.unitPrice),
            total_price: Number(item.totalPrice),
            product_value: item.productValue,
            category_id: 0,
            product_category: 0,
            payment_desc: item.productCode,
            payment_for: item.productName,
            // Add required OrderItem fields with default values or mapped values
            price: item.unitPrice, // or another appropriate value
            balance: 0, // Default or calculated value
            qty: Number(item.quantity), // or another appropriate value
            tranx_details: '', // Default or mapped value

            modifiers: [], // Default or mapped value
            checkout: false, // Default or mapped value
        }));

        const payload = {
            customerId: selectedCustomer?.id,
            userId: loggedInUser.id,
            items: orderItemsPayload,
            status: 'draft',
            amountToPay: Number(calculateOrderTotal),
            customer_name: selectedCustomer?.name,
            orderType: 'manual_order',
            tableId: '1', // Example, make dynamic if needed
        };

        try {
            let response;
            let currentOrderId = savedOrderId;

            if (currentOrderId) {
                if (!isAutoSave) console.log(`Updating draft order ID: ${currentOrderId}`);

                // console.log(`currentOrderId:`, currentOrderId);
                // console.log(`Saving order with payload:`, payload);

                response = await updateManualOrder(currentOrderId, payload);

            } else {
                if (!isAutoSave) console.log("Creating new draft order");
                response = await createOrder(payload);
                currentOrderId = response.data.id; // Assuming API returns { id: ... }
                setSavedOrderId(currentOrderId);
            }

            if (currentOrderId && `/new-order/draft/${currentOrderId}` !== window.location.pathname) {
                navigate(`/new-order/draft/${currentOrderId}`, { replace: true });
            }
            if (!isAutoSave) {
                toast.success(t('orderEdit.orderSavedAsDraft', { id: currentOrderId }) as string);
            } else {
                console.log(`Order auto-saved (ID: ${currentOrderId}) at ${new Date().toLocaleTimeString()}`);
                // Optionally, a less intrusive notification for auto-save
                // toast.info(`Order auto-saved (ID: ${currentOrderId})`, { autoClose: 2000 });
            }
            setIsDirty(false); // Reset dirty state after successful save   
        } catch (error: any) {
            console.error("Error saving order:", error);
            if (!isAutoSave) toast.error(t('orderEdit.errors.failedToSaveOrder', { error: error.message || t('errors.unknownError') }) as string);
        } finally {
            setIsSaving(false);
        }
    }, [selectedCustomer, orderItems, loggedInUser, calculateOrderTotal, savedOrderId, navigate]);

    // Effect to save the order right after an item is removed
    useEffect(() => {
        if (itemJustRemoved) {
            handleSaveOrder(true); // true for silent auto-save
            setItemJustRemoved(false); // Reset the flag
        }
    }, [itemJustRemoved, orderItems, handleSaveOrder]);

    // Effect to save the order right after an item's quantity or price is updated
    useEffect(() => {
        if (itemJustUpdated) {
            handleSaveOrder(true); // true for silent auto-save
            setItemJustUpdated(false); // Reset the flag
        }
    }, [itemJustUpdated, orderItems, handleSaveOrder]);


    // Effect to save the order right after an item is restored
    useEffect(() => {
        if (itemJustRestored) {
            handleSaveOrder(true); // true for silent auto-save
            setItemJustRestored(false); // Reset the flag
        }
    }, [itemJustRestored, orderItems, handleSaveOrder]);

    const handleRemoveOrderItem = (productId: number) => {
        // This updates the list of items you see on the screen.
        setIsDirty(true);

        setOrderItems(prevItems => prevItems.filter(item => item.productId !== productId));
        // This tells the component that an auto-save is needed.
        // Calling handleSaveOrder() directly here would use the OLD list of items.
        setItemJustRemoved(true);
        toast.success(t('orderEdit.itemRemovedSaving') as string);
    };

    const handleUpdateOrderItemQuantity = (productId: number, newQuantity: number) => {
        if (newQuantity < 1) newQuantity = 1;
        setIsDirty(true);


        setOrderItems(prevItems =>
            prevItems.map(item =>
                item.productId === productId
                    ? { ...item, quantity: newQuantity, totalPrice: newQuantity * item.unitPrice }
                    : item
            )
        );
        setItemJustUpdated(true); // Trigger auto-save
        toast.success(t('orderEdit.quantityUpdatedSaving') as string);
    };

    // Effect to save the order right after a new item is added
    useEffect(() => {
        if (itemJustAdded) {
            handleSaveOrder(true); // true for silent auto-save
            setItemJustAdded(false); // Reset the flag
        }
    }, [itemJustAdded, orderItems, handleSaveOrder]);

    const handleSubmitOrder = async () => {
        if (!selectedCustomer) {
            toast.error(t('orderEdit.errors.selectCustomerFirst') as string);
            return;
        }
        if (orderItems.length === 0) {
            toast.error(t('orderEdit.errors.addItemsToOrder') as string);
            return;
        }
        // Temporarily use handleSaveOrder to ensure data is up-to-date, then change status
        // A more robust solution would be an API endpoint that finalizes a draft.
        setIsSaving(true);
        try {
            // Ensure the latest state is saved as 'draft' first
            await handleSaveOrder(true); // Perform a silent save

            if (savedOrderId) {
                const finalPayload = {
                    customerId: selectedCustomer?.id,
                    userId: loggedInUser?.id,
                    items: orderItems.map(item => ({
                        product_id: item.productId,
                        product_name: item.productName,
                        product_code: item.productCode,
                        quantity: Number(item.quantity),
                        price: item.unitPrice,
                        total_price: Number(item.totalPrice),
                        product_value: item.productValue,
                    })),
                    status: 'manual_order', // Change status to submitted
                    amountToPay: Number(calculateOrderTotal),
                    customer_name: selectedCustomer?.name,
                    orderType: 'manual_order',
                    tableId: '1',
                };

                console.log(`Submitting order with payload:`, finalPayload);

                await updateManualOrder(savedOrderId, finalPayload);

                toast.success(t('orderEdit.orderSubmitted', { id: savedOrderId }) as string);
                // Clear state for a new order
                setSavedOrderId(null);
                setSelectedCustomer(null);
                setOrderItems([]);
                if (autoSaveTimerRef.current) clearInterval(autoSaveTimerRef.current);
                navigate('/order/' + savedOrderId, { replace: true }); // Navigate to a fresh order page
            } else {
                toast.error(t('orderEdit.errors.submitFailedSaveDraftFirst') as string);
            }
        } catch (error: any) {
            console.error("Error submitting order:", error);
            toast.error(t('orderEdit.errors.failedToSubmitOrder', { error: error.message || t('errors.unknownError') }) as string);
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        if (autoSaveTimerRef.current) {
            clearInterval(autoSaveTimerRef.current)
        }

        if (savedOrderId && selectedCustomer && orderItems.length > 0) {

            // console.log(`Setting up auto-save for order ID: ${savedOrderId}`);

            autoSaveTimerRef.current = setInterval(() => {
                handleSaveOrder(true); // Pass true for auto-save
            }, AUTO_SAVE_INTERVAL);
        }

        return () => {
            if (autoSaveTimerRef.current) {
                clearInterval(autoSaveTimerRef.current);
                console.log(`Cleared auto-save timer for order ID: ${savedOrderId}`);
            }
        };
    }, [savedOrderId, handleSaveOrder, selectedCustomer, orderItems]);



    // --- Functions for Deleted Items Modal ---
    const handleOpenDeletedItemsModal = async () => {
        if (!savedOrderId) {
            toast.info(t('orderEdit.noOrderForDeletedItems') as string);
            return;
        }
        setLoadingDeletedItems(true);
        setIsDeletedItemsModalVisible(true); // Open modal immediately
        try {
            const fetchedItems: OrderItem[] = await fetchSoftDeletedOrderItems(savedOrderId); // API returns OrderItem[]

            const itemsDataForModal: DeletedItemDisplay[] = fetchedItems.map(item => ({
                orderItemId: item.id!, // Assert item.id is present for deleted items (this is order_item.id)
                productId: Number(item.product_id),
                productName: item.payment_for || item.product_name,
                productCode: item.payment_desc || item.product_code,
                quantity: Number(item.qty),
                unitPrice: Number(item.unit_price),
                totalPrice: Number(item.unit_price) * Number(item.qty),
                productValue: Number(item.product_value), // SRP
                deletedAt: item.deleted_at ?? undefined,
            }));

            setDeletedOrderItems(itemsDataForModal);

        } catch (error) {
            console.error("Failed to fetch deleted items:", error);
            toast.error(t('orderEdit.errors.couldNotLoadDeleted') as string);
            setDeletedOrderItems([]); // Clear on error
        } finally {
            setLoadingDeletedItems(false);
        }
    };

    const handleRestoreOrderItem = async (orderItemIdToRestore: number) => {
        if (!savedOrderId) return;

        let newDeletedItemsList: DeletedItemDisplay[] = []; // To check length after update


        try {
            await restoreOrderItemAPI(orderItemIdToRestore); // API call to backend to restore

            // Refetch the entire draft order to get the updated active items list
            setIsLoadingDraft(true);
            const draftOrder = await fetchOrderDetails(savedOrderId);
            const updatedActiveItems: OrderItemData[] = draftOrder.items.map((item: OrderItem) => ({

                orderItemId: item.id, // <-- PRESERVE order_item.id

                productId: Number(item.product_id),
                productName: item.payment_for || item.product_name,
                productCode: item.payment_desc || item.product_code,
                quantity: Number(item.qty),

                unitPrice: Number(item.unit_price),
                totalPrice: Number(item.unit_price) * Number(item.qty),
                productValue: Number(item.product_value),
            }));

            setIsDirty(true);

            setOrderItems(updatedActiveItems);
            setIsLoadingDraft(false);

            // Remove the restored item from the local list of deleted items in the modal
            setDeletedOrderItems(prev => {
                newDeletedItemsList = prev.filter(item => item.orderItemId !== orderItemIdToRestore);
                return newDeletedItemsList;
            });

            toast.success(t('orderEdit.itemRestored') as string);
            // await handleSaveOrder(true); // Auto-save after restoring
            setItemJustRestored(true); // Trigger auto-save via useEffect

            // If the list of deleted items is now empty, close the modal
            if (newDeletedItemsList.length === 0) {
                setIsDeletedItemsModalVisible(false);
            }

        } catch (error) {
            console.error("Failed to restore item:", error);
            toast.error(t('orderEdit.errors.couldNotRestore') as string);
        }
    };

    if (isLoadingDraft) {
        // return <div className="container mx-auto p-4 text-center">Loading draft order...</div>;
        return <Spinner />; // Use the Spinner component

    }

    // console.log('savedOrderId', savedOrderId)

    const handleUpdateOrderItemUnitPrice = (productId: number, newUnitPrice: number) => {
        if (newUnitPrice < 0) newUnitPrice = 0;

        setIsDirty(true);

        setOrderItems(prevItems =>
            prevItems.map(item =>
                item.productId === productId
                    ? { ...item, unitPrice: newUnitPrice, totalPrice: newUnitPrice * item.quantity }
                    : item
            )
        );


        setItemJustUpdated(true); // Trigger auto-save
        toast.success(t('orderEdit.unitPriceUpdatedSaving') as string);

    };


    // Import the modal component
    const DeletedItemsModal = React.lazy(() => import('../components/DeletedItemsModal'));


    // --- Fulfillment Update Handler --- // ✅ FIX: Changed function to not accept arguments
    const handleUpdateFulfillment = async () => {
        const userId = loggedInUser?.id; // Acting user ID

        if (!userId) {
            toast.error("Cannot update fulfillment: Missing order or user details.");
            return;
        }

        setIsSavingFulfillment(true);

        // Construct the fulfillment object based on the current state
        const fulfillmentPayload = {
            fulfillment_type: fulfillmentType,
            ...(fulfillmentType === 'pickup' && {
                pickup_time_slot: pickupTime,
                picker_name: pickerName,
                picker_phone: pickerPhone,
                picker_car_plate: pickerCarPlate,
            }),
        };

        // Construct the main payload for the updateOrder API
        const orderPayload = {
            // ...order, // ✅ FIX: Start with existing order data to include all required fields
            userId: userId, // Ensure the acting user ID is present
            // If fulfillment is updated, reset status to 'pending' unless it's already 'completed'
            status: orderItems.length > 0 ? 'draft' : 'pending',
            orderType: 'manual_order',
            tableId: '1', // Assuming default table ID
            amountToPay: Number(calculateOrderTotal),
            customer_name: selectedCustomer?.name || 'Unknown Customer',
            fulfillment: fulfillmentPayload, // Add the new fulfillment data
        };

        try {
            const updatedOrderData = await updateOrder(Number(savedOrderId), orderPayload);

            // Update local state with the full response from the API
            const newFulfillment = updatedOrderData.data.fulfillment;
            if (newFulfillment) {
                setFulfillmentType(newFulfillment.fulfillment_type || 'delivery');
                setPickupTime(newFulfillment.pickup_time_slot || '');
                setPickerName(newFulfillment.picker_name || '');
                setPickerPhone(newFulfillment.picker_phone || '');
                setPickerCarPlate(newFulfillment.picker_car_plate || '');
            }

            toast.success("Fulfillment details updated successfully!");
            setIsFulfillmentModalOpen(false); // Close modal on success
        } catch (err: any) {
            console.error("Error updating fulfillment:", err);
            toast.error(`Failed to update fulfillment: ${err.message || 'Unknown error'}`);
        } finally {
            setIsSavingFulfillment(false);
        }
    };


    return (
        <div className=" mx-auto p-2">

            <div className="flex flex-col space-y-1 p-2 rounded-xl 
            fixed bottom-20 right-2 justify-end z-10 
            bg-neutral-100/80 hover:bg-neutral-300/30  text-neutral-600 backdrop-blur-[1px] border border-neutral-400/50
            md:fixed md:bottom-20 md:right-2 md:justify-end
            ">
                {orderItems.length > 0 && (
                    <div className="flex flex-row justify-between items-center border-t ">
                        <div className="text-sm md:text-md font-semibold">{t('orderEdit.summary.title')}</div>
                        <div className="text-sm md:text-md font-bold my-2">
                            {t('orderEdit.summary.total')}: {displayPrice(calculateOrderTotal)}
                        </div>

                    </div>
                )}

                <div className="flex space-x-3 ">
                    <button
                        onClick={() => handleSaveOrder(false)}
                        disabled={isSaving}
                        className={`bg-cyan-600 hover:bg-cyan-700 text-white text-xs md:text-sm font-bold py-2 px-6 rounded ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {isSaving && !savedOrderId ? t('buttons.saving') : (isSaving && savedOrderId ? t('buttons.updating') : (savedOrderId ? t('buttons.updateDraft', { id: savedOrderId }) : t('buttons.saveOrder')))}
                    </button>
                    <button
                        onClick={handleSubmitOrder}
                        disabled={isSaving || !savedOrderId} // Disable if no draft to submit
                        className={`bg-green-600 hover:bg-green-700 text-white  text-xs md:text-sm font-bold py-2 px-6 rounded ${(isSaving || !savedOrderId) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {t('buttons.submitOrder')}
                    </button>
                </div>
            </div>

            <h3 className="text-md md:text-md font-bold mb-2 pt-2  
            fixed top-10 left-2 z-20 w-full  bg-white shadow-md">
                {savedOrderId ? t('orderEdit.editingDraft', { id: savedOrderId }) : `${toCapitalCase(t('new'))} ${t('order.title')}`}
            </h3>

            {!selectedCustomer ? (
                <div className="bg-gray-100 p-2 rounded-lg shadow-md text-center  pt-16">
                    {/* <h3 className="text-xl font-semibold mb-4">Begin by selecting or creating a customer</h3> */}

                    <h3 className="text-xl font-semibold mb-4">
                        {savedOrderId && orderItems.length > 0
                            ? t('orderEdit.draftLoadedCustomerNeeded', { id: savedOrderId })
                            : t('orderEdit.selectOrCreateCustomer')}
                    </h3>

                    {savedOrderId && orderItems.length > 0 && (
                        <p className="text-sm text-gray-700 mb-4">{t('orderEdit.originalCustomerNotFound')}</p>
                    )}

                    <div className="flex justify-center space-x-4 mt-4">
                        <button
                            onClick={() => setIsCustomerSelectorVisible(true)}
                            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded inline-flex items-center"
                        >
                            <FiList className="mr-2" /> {t('buttons.selectExistingCustomer')}
                        </button>
                        <button
                            onClick={handleOpenCreateModal}
                            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded transition duration-200 w-full md:w-auto"
                        >
                            {t('buttons.newCustomer')} +
                        </button>
                    </div>


                    {/* Display loaded items if draft customer not found */}
                    {savedOrderId && orderItems.length > 0 && !selectedCustomer && (
                        <div className="mt-6 border-t pt-6 text-left">
                            <h4 className="text-lg font-semibold mb-3">{t('orderEdit.itemsInDraft')}:</h4>
                            <ol className="list-decimal list-inside space-y-2">
                                {orderItems.map(item => (
                                    <li key={item.productId} className="p-2 border  rounded-md bg-gray-50 flex flex-col md:flex-row justify-between md:items-center ">
                                        <div>
                                            <p className="font-medium">{item.productName}<br /><span className="text-xs text-gray-500">(SKU : {item.productCode})</span></p>
                                            <p className="text-sm text-gray-600">
                                                {displayPrice(item.unitPrice)} x {item.quantity} = <span className="font-semibold">{displayPrice(item.totalPrice)}</span>
                                            </p>
                                        </div>
                                        {/* Quantity input is not available here as it's part of the main edit form */}
                                        <div className="text-sm text-gray-600 mt-1 md:mt-0">
                                            {t('orderEdit.qtyLabel')}: {item.quantity}
                                        </div>
                                    </li>
                                ))}
                            </ol>
                            <div className="text-md font-bold my-2 text-right">
                                {t('orderEdit.draftTotal')}: {displayPrice(calculateOrderTotal)}
                            </div>
                            <p className="text-center text-blue-600 mt-2">
                                {t('orderEdit.selectCustomerToProceed')}
                            </p>
                        </div>
                    )}

                </div>
            ) : (
                <div className="mt-8 bg-white rounded-lg shadow-md">
                    <div className="flex flex-col md:flex-row  justify-between items-center pt-2 pb-1  sticky top-16 bg-white z-10 shadow-md">
                        <div className="text-sm font-semibold">
                            {t('orderEdit.orderFor')}: <span className="text-blue-600">{selectedCustomer?.name}</span>
                        </div>
                        <div className="flex flex-row w-full md:w-2/3 justify-between items-center pt-1 pb-2 sticky top-16 bg-white z-10">

                            <div className="text-sm text-gray-600  flex flex-col md:flex-row justify-between ">
                                <div className='mr-4'><strong>{t('labels.email')}:</strong> {selectedCustomer?.email || t('labels.notAvailable')}</div>
                                <div><strong>{t('labels.phone')}:</strong> {selectedCustomer?.phone || t('labels.notAvailable')}</div>
                            </div>
                            <div>
                                <button
                                    onClick={() => {
                                        setSelectedCustomer(null);
                                        setIsDirty(false);

                                        setOrderItems([]); // Also clear items if customer is changed
                                        setSavedOrderId(null); // Clear saved order ID
                                        if (autoSaveTimerRef.current) clearInterval(autoSaveTimerRef.current);
                                        navigate('/new-order', { replace: true }); // Go to a fresh new order page
                                    }}
                                    className="text-xs text-blue-500 hover:underline border border-blue-500 hover:bg-blue-50 rounded px-2 py-1 transition duration-200"
                                >
                                    {t('buttons.changeAndStartNew')}
                                </button>
                            </div>
                        </div>
                    </div>


                    <div className="mt-2 border-t pt-2">
                        {/* <h3 className="text-lg font-semibold mb-4">Order Items</h3> */}

                        <div className="flex justify-between items-center pb-4 sticky top-36 md:top-28 md:pt-4  bg-white z-10 shadow-md">
                            <div className="text-sm font-semibold text-blue-500">{t('orderEdit.orderItems')}</div>
                            <div className="text-sm text-gray-500 transition-opacity duration-300 h-5">
                                {isSaving ? (
                                    <span className="flex items-center animate-pulse">{t('status.saving')}...</span>
                                ) : !isDirty && savedOrderId ? (
                                    <span className="text-green-600 font-medium">{t('status.allChangesSaved')}</span>
                                ) : null}
                            </div>
                        </div>

                        {orderItems.length === 0 ? (
                            <p className="text-gray-500">{t('orderEdit.noItemsYet')}</p>
                        ) : (
                            <div className="space-y-3 pb-56 md:pb-46 bg-gray-100 ">
                                <ol className="list-decimal list-inside space-y-1">
                                    {orderItems.map((item, idx) => (
                                        <li key={item.productId} className="flex flex-col md:flex-row items-start md:items-center justify-between 
                                         p-3 border border-b-gray-300  rounded-md bg-gray-200 space-y-1  relative pl-6 text-[0.8rem] md:text-[0.9rem]">
                                            <span className="absolute left-0 top-4 text-gray-400 font-bold">{idx + 1}.</span>

                                            <div className='flex-grow w-full md:w-5/12'>
                                                <p className="font-medium">{item.productName}<br /><span className="text-[0.8rem]  text-gray-500">(SKU : {item.productCode})</span></p>
                                            </div>
                                            <div className="flex items-center space-x-2 w-full md:w-auto">
                                                {/* <p className="w-24 p-1 border rounded text-center bg-white">{displayPrice(item.unitPrice)}</p> */}
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={item.unitPrice}
                                                    onChange={e => handleUpdateOrderItemUnitPrice(item.productId, parseFloat(e.target.value) || 0)}
                                                    className="w-24 p-1 border rounded text-center bg-white"
                                                    style={{ MozAppearance: 'textfield' }}
                                                />
                                                <span className="text-gray-500">x</span>
                                                <input
                                                    type="number"
                                                    value={item.quantity}
                                                    onChange={(e) => handleUpdateOrderItemQuantity(item.productId, parseInt(e.target.value, 10))}
                                                    className="w-16 p-1 border rounded text-center"
                                                    min="1"
                                                />
                                                <span className="text-gray-500">=</span>
                                                <p className="w-24 p-1 border rounded text-center bg-white font-semibold">{displayPrice(item.totalPrice)}</p>
                                            </div>
                                            <div className="flex-shrink-0">
                                                <button onClick={() => handleRemoveOrderItem(item.productId)} className="p-2 text-red-500 hover:text-red-700 flex items-center justify-center w-8 h-8 rounded-full hover:bg-red-100 transition duration-200">
                                                    <FiTrash2 size={18} />
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        )}


                        <FulfillmentModal
                            isOpen={isFulfillmentModalOpen}
                            onClose={() => setIsFulfillmentModalOpen(false)}
                            fulfillmentType={fulfillmentType}
                            setFulfillmentType={setFulfillmentType}
                            pickupTime={pickupTime}
                            setPickupTime={setPickupTime}
                            pickerName={pickerName}
                            setPickerName={setPickerName}
                            pickerPhone={pickerPhone}
                            setPickerPhone={setPickerPhone}
                            pickerCarPlate={pickerCarPlate}
                            setPickerCarPlate={setPickerCarPlate} // ✅ FIX: Pass the function by reference
                            handleUpdateFulfillment={handleUpdateFulfillment}
                            isSavingFulfillment={isSavingFulfillment}
                            isActionInProgress={isActionInProgress}
                        />


                        <div className='
                        mt-1 
                        fixed bottom-40 right-0 z-5 pt-1 pb-4 px-2  
                        md:bottom-16 md:left-0 md:right-56 
                        bg-neutral-100/80 hover:bg-neutral-300/30  text-neutral-600 backdrop-blur-[1px] border border-neutral-400/50'>

                            <button
                                onClick={() => setIsProductSelectorVisible(true)}
                                className="mt-1 bg-indigo-500 hover:bg-indigo-600 text-white  text-xs md:text-xs font-bold py-2 px-4 rounded inline-flex items-center
                                "
                            >
                                <FiPlusCircle className="mr-2" /> {t('buttons.addItem')}
                            </button>

                            {savedOrderId && ( // Only show if the order has been saved at least once
                                <button
                                    onClick={handleOpenDeletedItemsModal}
                                    className="mt-1 ml-2 bg-yellow-500 hover:bg-yellow-600 text-black  text-xs md:text-xs font-bold py-2 px-4 rounded inline-flex items-center"
                                    disabled={isSaving || isLoadingDraft} // Disable if saving or loading main draft
                                >
                                    <FiRotateCcw className="mr-2" /> {t('buttons.viewDeleted')}
                                </button>
                            )}

                            <button
                                onClick={() => setIsFulfillmentModalOpen(true)} className="ml-2 mt-1 bg-gray-800 hover:bg-gray-900 text-white  text-xs md:text-xs font-bold py-2 px-4 rounded inline-flex items-center"
                            >
                                <svg className="w-4 h-3 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>

                                Fulfillment
                            </button>
                        </div>

                    </div>



                </div>
            )}

            {isCustomerSelectorVisible && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
                    <div className="bg-white p-2 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-semibold">{t('customerSelector.title')}</h3>
                            <button onClick={() => setIsCustomerSelectorVisible(false)} className="text-gray-500 hover:text-gray-800">
                                <FiX size={24} />
                            </button>
                        </div>
                        <input
                            type="text"
                            placeholder={t('customerSelector.searchPlaceholder')}
                            value={customerSearchTerm}
                            onChange={handleCustomerSearchChange}
                            className="w-full p-2 border border-gray-300 rounded mb-4"
                        />
                        {loadingAllCustomers && <p>{t('customerSelector.loading')}</p>}
                        {errorAllCustomers && <p className="text-red-500">{t('customerSelector.error')}: {errorAllCustomers}</p>}
                        <div className="overflow-y-auto flex-grow">
                            {currentCustomersForSelection.length > 0 ? (
                                <ul className="divide-y divide-gray-200">
                                    {currentCustomersForSelection.map(customer => (
                                        <li key={customer?.id}
                                            onClick={() => handleSelectCustomer(customer)}
                                            className="p-3 hover:bg-gray-100 cursor-pointer rounded"
                                        >
                                            <p className="font-medium">{customer?.name}</p>
                                            <p className="text-sm text-gray-500">{customer?.email} - {customer?.phone || t('customerSelector.noPhone')}</p>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                !loadingAllCustomers && <p className="text-gray-500 text-center py-4">{t('customerSelector.noCustomersFound')}</p>
                            )}
                        </div>
                        {totalCustomerPages > 1 && (
                            <div className="mt-4 pt-4 border-t">
                                <Pagination
                                    currentPage={customerCurrentPage}
                                    totalPages={totalCustomerPages}
                                    onPageChange={setCustomerCurrentPage}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {isCreateModalOpen && (
                <CreateCustomerModal
                    isOpen={isCreateModalOpen}
                    onClose={handleCloseCreateModal}
                    onCreateCustomer={handleCreateCustomer}
                />
            )}

            {isProductSelectorVisible && selectedCustomer && (
                <ProductSelectionModal
                    isOpen={isProductSelectorVisible}
                    onClose={() => setIsProductSelectorVisible(false)}
                    onProductSelect={handleAddProductToOrder}
                    customer={selectedCustomer}
                />
            )}


            {/* Modal for viewing/restoring deleted items */}
            <React.Suspense fallback={<div>{t('loadingModal')}</div>}>
                {isDeletedItemsModalVisible && (
                    <DeletedItemsModal
                        isOpen={isDeletedItemsModalVisible}
                        onClose={() => setIsDeletedItemsModalVisible(false)}
                        items={deletedOrderItems}
                        onRestoreItem={handleRestoreOrderItem}
                        loading={loadingDeletedItems}
                    />
                )}
            </React.Suspense>

        </div>
    );
};


// --- ProductSelectionModal (no changes here, but included for context) ---
const ProductSelectionModal: React.FC<ProductSelectionModalProps> = ({ isOpen, onClose, onProductSelect, customer }) => {
    const { t } = useTranslation();
    const [products, setProducts] = useState<Product[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [errorProducts, setErrorProducts] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);


    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);


    useEffect(() => {
        if (!isOpen) return;
        const loadProducts = async () => {
            setLoadingProducts(true);
            setErrorProducts(null);

            try {
                const { products: fetchedProducts, total_pages } = await fetchProducts4manual(
                    currentPage, searchTerm, null, null, PRODUCTS_PER_PAGE_MODAL, 'updated_at DESC',
                    // 0 // No need for category filter in modal
                );
                setProducts(fetchedProducts);
                setTotalPages(total_pages);
            } catch (err: any) {
                setErrorProducts(err.message || 'Failed to load products.');
            } finally {
                setLoadingProducts(false);
            }
        };
        loadProducts();
    }, [isOpen, currentPage, searchTerm]);

    const handleSelect = async (product: Product) => {
        let priceToUse = product.product_value;
        try {
            if (customer && customer?.user_id && customer?.role) {
                const priceData = await fetchMtPrice(product.id, customer?.user_id, customer?.role);
                if (priceData && priceData[product.id]?.price !== undefined) {
                    priceToUse = priceData[product.id].price;
                } else {
                    toast.info(t('productSelector.noSpecialPrice', { productName: product.product_name, customerName: customer?.name }) as string);
                }
            } else {
                toast.warn(t('productSelector.customerDetailsMissing', { productName: product.product_name }) as string);
            }
        } catch (priceError) {
            console.error("Failed to fetch MT price for order item:", priceError);
            toast.warn(t('productSelector.fetchSpecialPriceFailed', { productName: product.product_name }) as string);
        }
        onProductSelect(product, 1, priceToUse);
    };

    if (!isOpen) return null;

    const handleCreateNewItem = async () => {
        onClose();
        // You can open a modal, or call a callback, or navigate to a product creation page
        // toast.info(`Create new product: "${searchTerm}"`);

        if (!searchTerm.trim()) {
            toast.error(t('productSelector.productNameEmpty') as string);
            return;
        }

        // TODO: Implement a way to get category_id and product_value (SRP) from the user.
        // For now, using placeholder/default values.
        const defaultCategoryId = 1; // Placeholder: e.g., 'Uncategorized' or first available
        const defaultProductValue = 0.00; // Placeholder: SRP

        toast.warn(
            t('productSelector.usingDefaultsForNewProduct', { categoryId: defaultCategoryId, srp: displayPrice(defaultProductValue) }) as string,
            { autoClose: 7000 }
        );

        const payload = {
            product_name: searchTerm,
            product_cat: defaultCategoryId,
            product_value: defaultProductValue,
            // product_code can be omitted if backend generates it
        };

        try {
            const response = await createNewProductItem(payload);

            toast.success(t('productSelector.productCreated', { name: response.product.product_name }) as string);

            onProductSelect(response.product, 1, response.product.product_value); // Add to order

        } catch (error: any) {
            console.error("Failed to create product:", error);
            toast.error(t('productSelector.createFailed', { error: error.response?.data?.message || error.message || t('errors.unknownError') }) as string);
        }

    };


    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">


            <div className="bg-white p-2 rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold">{t('productSelector.title')}</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><FiX size={24} /></button>
                </div>

                <div className="relative flex items-center mb-4">
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder={t('productSelector.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        className="w-full p-2 border border-gray-300 rounded"
                        onKeyDown={e => {
                            if (searchTerm && e.key === 'Enter') {
                                e.preventDefault();
                                handleCreateNewItem();
                            }
                        }}
                        autoComplete="off"
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={handleCreateNewItem}
                            className="ml-2 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-xs font-semibold"
                        >
                            + {t('buttons.new')}
                        </button>
                    )}

                    {/* Autocomplete suggestions */}
                    {searchTerm && products.length > 0 && (
                        <ul className="absolute left-0 top-full mt-1 w-full bg-green-100 border border-gray-200 rounded shadow-lg z-10 max-h-60 overflow-y-auto ">
                            {products.map(product => (
                                <li
                                    key={product.id}
                                    onClick={() => handleSelect(product)}
                                    className="p-1 hover:bg-blue-100 cursor-pointer"
                                >
                                    <span className="text-xs font-medium">{product.product_name}</span>
                                    <span className="text-xs text-gray-500 ml-2">({product.product_code})</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {loadingProducts && <p>{t('productSelector.loading')}</p>}
                {errorProducts && <p className="text-red-500">{t('productSelector.error')}: {errorProducts}</p>}
                <div className="overflow-y-auto flex-grow">
                    {products.length > 0 ? (
                        <ul className="divide-y divide-gray-200">
                            {products.map(product => (
                                <li key={product.id} onClick={() => handleSelect(product)} className="p-3 hover:bg-gray-100 cursor-pointer rounded">
                                    <p className="text-sm font-thin">{product.product_name} <span className="text-xs text-gray-500">({product.product_code})</span></p>
                                    <p className="text-sm text-gray-600">{t('productSelector.srpLabel')}: {displayPrice(product.product_value)}</p>
                                </li>
                            ))}
                        </ul>
                    ) : (!loadingProducts && (
                        <div className="text-center py-4">
                            <p className="text-gray-500">{t('productSelector.noProductsFound', { searchTerm })}</p>
                            {searchTerm && ( // Only show if there was a search term
                                <p className="text-sm text-blue-500 mt-2">{t('productSelector.canCreateNew')}</p>
                            )}
                        </div>
                    ))}
                </div>

                {totalPages > 1 && (
                    <div className="mt-4 pt-4 border-t"><Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} /></div>
                )}
            </div>
        </div>
    );
};



export default OrderEdit;
